import { useMemo, useState } from 'react';
import { ChevronRight, Search, Satellite } from 'lucide-react';
import { useLoader } from '../store';
import { useUI } from '@/state/store';
import { useSSA } from './store';
import { VirtualList } from './VirtualList';
import type { Mission } from '../types';
import { catalogueCountry } from './countries';
type Row = { key: string; depth: number; name: string; count?: number; mission?: Mission };
export function CatalogueStrip({ expanded, onOpenRequest, onPicked }: { expanded: boolean; onOpenRequest: () => void; onPicked: () => void }) {
  const dataset = useLoader(s => s.dataset);
  const selected = useUI(s => s.selection.missionId);
  const search = useSSA(s => s.search);
  const [opened, setOpened] = useState(new Set<string>());
  const rows = useMemo(() => {
    const groups = new Map<string, Map<string, Mission[]>>();
    for (const mission of dataset?.missions ?? []) {
      const o = mission.trackedObject;
      if (!o || (search && !`${mission.name} ${o.id} ${o.internationalId} ${o.country} ${catalogueCountry(o.country)} ${o.family}`.toLowerCase().includes(search.toLowerCase()))) continue;
      const country = catalogueCountry(o.country);
      const family = o.kind === 'debris' ? 'Debris' : o.kind === 'rocket-body' ? 'Rocket bodies' : o.family;
      if (!groups.has(country)) groups.set(country, new Map());
      const sub = groups.get(country)!;
      if (!sub.has(family)) sub.set(family, []);
      sub.get(family)!.push(mission);
    }
    const out: Row[] = [];
    for (const [country, families] of [...groups].sort(([a], [b]) => a.localeCompare(b))) {
      out.push({ key: country, depth: 0, name: country, count: [...families.values()].reduce((sum, m) => sum + m.length, 0) });
      if (!search && !opened.has(country)) continue;
      for (const [family, missions] of [...families].sort(([a], [b]) => a.localeCompare(b))) {
        const key = `${country}/${family}`;
        out.push({ key, depth: 1, name: family, count: missions.length });
        if (!search && !opened.has(key)) continue;
        for (const mission of missions.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))) out.push({ key: mission.id, depth: 2, name: mission.name, mission });
      }
    }
    return out;
  }, [dataset, search, opened]);
  return <div className="flex-1 overflow-hidden py-2">
    <button className="flex h-11 w-full items-center gap-3 px-[23px] text-xs text-accent" onClick={onOpenRequest} aria-label="Catalogue objects"><Satellite className="h-[18px] w-[18px] shrink-0" />{expanded && <>Objects <span className="ml-auto text-[10px]">{dataset?.missions.length.toLocaleString()}</span></>}</button>
    {expanded && <>
      <label className="mx-3 flex items-center gap-2 rounded border border-hairline bg-canvas px-2"><Search className="h-3 w-3 text-ink-3" /><input aria-label="Search catalogue" value={search} onChange={e => useSSA.setState({ search: e.target.value })} className="min-w-0 w-full bg-transparent py-2 text-xs" placeholder="Name, catalogue ID, family" /></label>
      <p className="px-3 py-2 text-[10px] text-ink-3">Reported country / organisation · spacecraft family. Includes hidden debris.</p>
      <VirtualList itemKey={row=>row.key} items={rows} rowHeight={32} height={600} label="Grouped orbital catalogue" render={row => <button className={`flex h-8 w-full items-center gap-1 pr-2 text-left text-[11px] hover:bg-overlay ${row.mission?.id === selected ? 'text-accent' : 'text-ink-2'}`} style={{ paddingLeft: 10 + row.depth * 10 }} aria-expanded={!row.mission ? opened.has(row.key) || !!search : undefined} title={row.mission ? `${row.name} · #${row.mission.noradId}` : row.name} onClick={() => {
        if (row.mission) {
          const o = row.mission.trackedObject!;
          if (o.kind === 'debris' || o.kind === 'rocket-body') useSSA.setState({ debris: true });
          useUI.getState().select({ missionId: row.mission.id, antennaId: null, assetId: null });
          useSSA.setState({ focusRequest: useSSA.getState().focusRequest + 1 }); onPicked();
        } else setOpened(old => { const next = new Set(old); if (next.has(row.key)) next.delete(row.key); else next.add(row.key); return next; });
      }}>{!row.mission && <ChevronRight className={`h-3 w-3 shrink-0 ${opened.has(row.key) || search ? 'rotate-90' : ''}`} />}<span className="truncate">{row.name}</span><span className="ml-auto text-[9px] text-ink-3">{row.count?.toLocaleString()}</span></button>} />
    </>}
  </div>;
}
